import axios from 'axios';
import { authService } from '../services/authService';
import { getRefreshToken, setAuthToken, setRefreshToken } from './storage';

// Shared across every concurrent 401 — from the axios interceptor as well as
// fetch-based callers like forceLogoutGuard's polling — so simultaneous
// failures trigger only one /auth/refresh call instead of one each.
let refreshInFlight: Promise<string | null> | null = null;

const doRefresh = async (): Promise<string | null> => {
  const storedRefreshToken = getRefreshToken();
  if (!storedRefreshToken) return null;

  try {
    const response = await authService.refreshToken(storedRefreshToken);
    const { token, refreshToken: newRefreshToken } = response.data;
    if (!token) return null;

    setAuthToken(token);
    if (newRefreshToken) {
      setRefreshToken(newRefreshToken);
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    return token;
  } catch {
    return null;
  }
};

/**
 * Refreshes the access token, deduping concurrent callers into a single
 * in-flight /auth/refresh request. Resolves to the new access token, or null
 * if there's no refresh token stored or the refresh call itself failed.
 */
export const getRefreshedAccessToken = (): Promise<string | null> => {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};
